/**
 * Detail-page content keyed by slug.
 * Add a new entry here to give any project a full detail page using the same
 * ProjectDetail component (see /components/portfolio/ProjectDetail.jsx).
 *
 * Shape:
 *   hero:        { image, caption? }       — top large editorial image
 *   secondary:   { image, caption? }       — image that sits beside the description
 *   description: string[]                  — paragraphs (rendered with spacing between)
 *   credit:      string                    — bottom-right credit line
 *   garments:    [{ label, image, note? }] — bottom row of garment / product photos
 */

export const projectDetails = {
  "highschool-fantasy": {
    hero: {
      image:
        "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/4x6vvyxm_IMG_1974.JPG",
      caption: "Cheerleader · Geek · Skater Girl — front-stoop trio",
    },
    secondary: {
      image:
        "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/n18rd7mq_side%20imagey0262343.jpg",
      caption: "Backstage rave wall · Mia Cho, 2026",
    },
    description: [
      "This collection reimagines American classic high-school uniforms through the lens of rave culture, expressed across three distinct personas: the Cheerleader, the Geek, and the Skater Girl.",
      "Growing up in Korea, I often fantasized about American high-school life — influenced by films such as Clueless, Mean Girls, and Gossip Girl. At the same time, I experienced Korea's highly disciplined and regulated school system, which quietly fostered a sense of rebellion within me.",
      "This internal tension between conformity and resistance is translated through the integration of rave culture — a space historically associated with freedom and subversion. By combining classic preppy silhouettes with experimental weaving and knitting techniques, this collection visualizes my imagined high-school fantasy.",
    ],
    credit: "Mia Cho",
    process: {
      eyebrow: "Sketchbook · Lineup",
      heading: {
        before: "10 looks,",
        accent: "3 personas",
        accentColor: "pink",
        after: ".",
      },
      intro:
        "Every garment in the collection started as a hand-drawn study — fabric swatches pinned to figure templates, persona labels scribbled in the margin. Cheerleader, Geek, Skater Girl: each look is tagged so the lineup reads like a yearbook page.",
      images: [
        {
          src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/9j2rje57_portfolio_000.jpg",
          caption: "Initial research — Korean high-school silhouette study",
        },
        {
          src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/64pizufx_Portfolio_00.jpg",
          caption: "Preppy archive · uniform history & textile direction",
        },
        {
          src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/573w7pfd_portfolio_1.jpg",
          caption: "Rave culture references · color & subculture",
        },
        {
          src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/ex8ukklv_sketchbook_4.jpg",
          caption: "The virtues of pink & green · color & fabric study",
        },
        {
          src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/wz5oe2tw_sketchbook_5.jpg",
          caption: "Tech pack · Geek jacket + reversible knit skirt construction",
        },
        {
          src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/svfdb3b8_sketchbook_6.jpg",
          caption: "Final fitting · Geek look · front + back styling",
        },
        {
          src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/4fn10fr6_sketchbook_7.jpg",
          caption: "Character 2 · Skater Girl research — DIY sticker culture & Vogue NY (Sep 1992)",
        },
        {
          src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/us65tyo9_sketchbook_8.jpg",
          caption: "Flying-tie shirt fittings · weaving the tie into the placket",
        },
        {
          src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/irmp0sxf_sketchbook_9.jpg",
          caption: "Tech pack · Skater Girl 'Flying Tie' shirt + sticker culottes",
        },
        {
          src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/lawkzr1c_sketchbook_10.jpg",
          caption: "Final fitting · Skater Girl look · pink shirt + sticker shorts + skateboard",
        },
        {
          src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/rwzvyej1_sketchbook_12.jpg",
          caption: "Character 1 · Cheerleader research — pom-pom geometry & reflective fabric studies",
        },
        {
          src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/00arvf87_sketchbook_13.jpg",
          caption: "Reflective fabric + neon · woven check fringe top development & Vogue Rave Review (Dec 1997)",
        },
        {
          src: "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/zmbbxt94_sketchbook_14.jpg",
          caption: "Final fitting · Cheerleader look · woven crop top + iridescent fringe mini",
        },
      ],
      footnote: "Pencil, marker, woven swatches · Mia Cho atelier, 2026",
    },
    shop: [
      {
        id: "hf-tweed-jacket",
        brand: "Mia Cho Studio",
        name: "Houndstooth Tweed Cropped Jacket",
        price: null,
        currency: "USD",
        accent: "#FF007F",
        packshot:
          "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/eqjuizkp_jacket.png",
        model:
          "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/6l62m3zc_MIA_location_may15-230.JPG",
      },
      {
        id: "hf-pink-shirt",
        brand: "Mia Cho Studio",
        name: "Pink Silk Skater Shirt",
        price: null,
        currency: "USD",
        accent: "#FF007F",
        packshot:
          "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/jn890io8_shirts.png",
        model:
          "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/98cherfq_MIA_parsons_May20261965.jpg",
      },
      {
        id: "hf-fringe-top",
        brand: "Mia Cho Studio",
        name: "Iridescent Fringe Halter Top",
        price: null,
        currency: "USD",
        accent: "#39FF14",
        packshot:
          "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/roe0tw5a_pants.png",
        model:
          "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/tddg0jdg_skater.jpg",
      },
      {
        id: "hf-checker-skirt",
        brand: "Mia Cho Studio",
        name: "Hand-Woven Checker Mini Skirt",
        price: null,
        currency: "USD",
        accent: "#FF007F",
        packshot:
          "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/tl72kbq1_skirt%20front_1.png",
        model:
          "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/d0cqmbpd_IMG_1990.JPG",
      },
    ],
  },

  /* ---------- Liquid Sky (2025) — stub, fill images/text over time ---------- */
  "liquid-sky": {
    hero: {
      image:
        "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/tn1oagik_Concept%202_%20408.webp",
      caption: "Liquid Sky · costume direction",
    },
    secondary: {
      image:
        "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/tn1oagik_Concept%202_%20408.webp",
      caption: "Chrome flower fitting · 2025",
    },
    description: [
      "Project. Liquid Sky is an experimental project that merges traditional couture techniques with advanced 3D-printing technology. It explores the intersection of craftsmanship and digital innovation, creating futuristic forms and textures while maintaining wearability and practicality.",
      "Inspired by the 80s sci-fi movie Liquid Sky (1986), this project translates its surreal atmosphere into bold geometric silhouettes and sharp, structured forms. Through a lens of retro-futurism, it reimagines 1980s sci-fi aesthetics into contemporary fashion.",
    ],
    credit: "Mia Cho",
    process: {
      eyebrow: "Sketchbook · Process",
      heading: {
        before: "Weather,",
        accent: "wearable",
        accentColor: "lime",
        after: ".",
      },
      intro:
        "Runway lineup, mood board, and tech packs — 80s Si-Fi Liquid Sky (1986) meets couture craft + 3D print. Drag or use arrows to flip through the plates.",
      images: [
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/cs516hc2_%E1%84%86%E1%85%A5%E1%84%85%E1%85%B5%20%E1%84%8C%E1%85%AE%E1%86%AF%E1%84%8B%E1%85%B5%E1%86%AB%E1%84%80%E1%85%A5.webp",
          caption: "Runway lineup — 5 looks · Liquid Sky S/S 2025",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/gkmd4yrs_cfdsadfga.webp",
          caption: "Project statement · Mood board — Slava Sivak, Thierry Mugler, Issey Miyake, Ungaro",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/t7nwt0gb_dsfv%404x.webp",
          caption: "Tech pack · Look 3 — Blouse + skirt, silk & sheer polyester with feather cluster",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/ipjjp2ma_p2%20development-2%404x.webp",
          caption: "Tech pack · Look 1 — Mini black dress, 3D-printed columns on silk organza",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/dsylcv63_ss%404x.webp",
          caption: "Tech pack · Look 4 — Sleeveless long coat with layered flying panels, 3D-printed necklace",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/5aqpt6bi_%E1%84%83%E1%85%A2%E1%84%8C%E1%85%B5%2028%404x.webp",
          caption: "Full collection lineup — 6 looks · 'Una obra maestra de Sexoficcion — Liquid Sky' · material notes & PLA fidget cubes",
        },
      ],
      footnote: "Craft × digital innovation · Mia Cho, 2025",
    },
    shop: [
      {
        id: "ls-look-1",
        brand: "Mia Cho Studio",
        name: "Liquid Sky — Look 1",
        price: null,
        accent: "#39FF14",
        packshot: null,
        model: null,
        images: [
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/yl2l6125_Concept%202_%202.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/l28hyv7j_Concept%202_%2085.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/0evpykr1_Concept%202_%20109.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/oasoga4j_Concept%202_%20137.webp",
        ],
      },
      {
        id: "ls-look-2",
        brand: "Mia Cho Studio",
        name: "Liquid Sky — Look 2",
        price: null,
        accent: "#39FF14",
        packshot: null,
        model: null,
        images: [
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/1k83la00_Concept%202_%20493.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/gh2jyii1_Concept%202_%20524.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/v53kj3gd_Concept%202_%20543.webp",
        ],
      },
      {
        id: "ls-look-3",
        brand: "Mia Cho Studio",
        name: "Liquid Sky — Look 3",
        price: null,
        accent: "#39FF14",
        packshot: null,
        model: null,
        images: [
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/ykye3i9w_Concept%202_%20295.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/3j2906ib_Concept%202_%20309.jpg",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/gp40fhq1_Concept%202_%20321.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/t9syg9vn_Concept%202_%20341.webp",
        ],
      },
      {
        id: "ls-look-4",
        brand: "Mia Cho Studio",
        name: "Liquid Sky — Look 4",
        price: null,
        accent: "#39FF14",
        packshot: null,
        model: null,
        images: [
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/0tpawxre_Concept%202_%20260.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/2p5qfstb_Concept%202_%20277.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/gmuram7g_Concept%202_%20160.webp",
        ],
      },
      {
        id: "ls-look-5",
        brand: "Mia Cho Studio",
        name: "Liquid Sky — Look 5",
        price: null,
        accent: "#39FF14",
        packshot: null,
        model: null,
        images: [
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/hmuv84rb_Concept%202_%20647.webp",
        ],
      },
    ],
  },

  /* ---------- Biba Queen (2025) — stub, fill images/text over time ---------- */
  "biba-queen": {
    hero: {
      image:
        "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/1o964o0g_Basic_%20199.jpg",
      caption: "Biba Queen · runway couture",
    },
    secondary: {
      image:
        "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/1o964o0g_Basic_%20199.jpg",
      caption: "Studio fitting · 2025",
    },
    description: [
      "A love letter to Barbara Hulanicki served on a Seoul subway platform. Velvet, vinyl, and feathers heavy enough to cause weather.",
      "The collection reframes the Biba silhouette through late-night commuter energy — swapping London Kensington for Hongdae Line 2, keeping the drama, adding chrome hardware and imported subway grit.",
    ],
    credit: "Mia Cho",
    process: {
      eyebrow: "Sketchbook · Process",
      heading: {
        before: "Biba,",
        accent: "rebuilt",
        accentColor: "pink",
        after: ".",
      },
      intro:
        "Process pages, mood references, and material tests will appear here — same carousel format as Highschool Fantasy. Placeholder plates are reserved below; new pages will replace them as they're added.",
      images: [
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/qjcklyhc_%E1%84%89%E1%85%B3%E1%84%8F%E1%85%B3%E1%84%85%E1%85%B5%E1%86%AB%E1%84%89%E1%85%A3%E1%86%BA%202026-08-21%2022.26.02.webp",
          caption: "Runway lineup — 4 looks · Biba Queen S/S 2025",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/7jpbxyul_%E1%84%89%E1%85%B3%E1%84%8F%E1%85%B3%E1%84%85%E1%85%B5%E1%86%AB%E1%84%89%E1%85%A3%E1%86%BA%202026-08-21%2022.42.05.webp",
          caption: "Backstage detail — puffed silhouettes, pleated spirals, ripple sculpture close-ups",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/14l6juea_res-1%404x.webp",
          caption: "Mood board · 1960s album posters + Art Nouveau motifs + Biba window pattern",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/q9lc31d0_res-3%404x.webp",
          caption: "Fabric ideas · Acephala / Muscheln shell prototypes — pleats + wire, finishing tests, laser cutting",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/flinp3lo_res-4%404x.webp",
          caption: "Fabric swatch library · square pleats, spirals, curved wire, feather + fur + peacock embroidery",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/bbnox5nk_%E1%84%89%E1%85%B3%E1%84%8F%E1%85%B3%E1%84%85%E1%85%B5%E1%86%AB%E1%84%89%E1%85%A3%E1%86%BA%202026-08-21%2022.47.28.webp",
          caption: "Silhouette development · Biba pattern-cutting, bouncy skirt studies, Bill Gibb + trapeze references",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/o7ndd07k_IMG_2903.webp",
          caption: "Research · new wave of feminism & Biba identity — Rudi Gernreich 'No Bra' (1969), 1960s–70s feminism notes",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/6slx0qz1_IMG_2901.webp",
          caption: "Concept · bedding combined with fashion — sense of sensuality, puffer volume, no-bra sculpture",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/mwzrs4ns_IMG_2911.webp",
          caption: "Prototype grid · dress iterations 1–3 · bias tape + wire spiral development",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/jm61ouis_IMG_2915.webp",
          caption: "Hoodie / vest development · Twiggy 'The Boy Friend' + snap-button construction",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/tigword7_IMG_2919.webp",
          caption: "Look 1 — Red · front / side / back fittings, color palette + pleated swatch",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/1q83cfw8_IMG_2921.webp",
          caption: "Look 2 — Blue · puffed bolero + spiral appliqué · fabric direction (aster / inner / cyan)",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/e5mhx0s2_IMG_2922.webp",
          caption: "Look 3 — Movement study · hummingbird references + walking sequence + spiral peel prints",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/j10o1ple_IMG_2923.webp",
          caption: "Sculptural spiral prototypes 1–3 · wire-heat coil method + spiral-shape notes",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/zz35dikd_IMG_2930.webp",
          caption: "Look 4/5 fitting · stretch-fit body-sculpt bodice, front + side/back, swatch direction",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/u6ekio4i_sixeup%404x.webp",
          caption: "Backstage close-up · 4 detail crops across the collection",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/fnknqlsy_photoshoot_1%404x.webp",
          caption: "Final photoshoot · 4-look lineup on ribbed wood backdrop",
        },
      ],
      footnote: "Pages coming soon · Mia Cho atelier",
    },
    shop: [
      {
        id: "bq-look-1",
        brand: "Mia Cho Studio",
        name: "Biba Queen — Look 1",
        price: null,
        accent: "#FF007F",
        packshot: null,
        model: null,
        images: [
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/yufjri5n_Basic_%20169.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/4mpoyqjw_Basic_%20210.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/laojcxbs_Concept%203_%20418.webp",
        ],
      },
      {
        id: "bq-look-2",
        brand: "Mia Cho Studio",
        name: "Biba Queen — Look 2",
        price: null,
        accent: "#FF007F",
        packshot: null,
        model: null,
        images: [
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/oasi4b4z_Basic_%2019.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/y9abpi57_Basic_%2011.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/iaixl707_Basic_%209.webp",
        ],
      },
      {
        id: "bq-look-3",
        brand: "Mia Cho Studio",
        name: "Biba Queen — Look 3",
        price: null,
        accent: "#FF007F",
        packshot: null,
        model: null,
        images: [
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/0qxedtfp_Basic_%2036.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/5iu5ebkz_Basic_%2079.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/qxvnsicm_Basic_%2044.webp",
        ],
      },
      {
        id: "bq-look-4",
        brand: "Mia Cho Studio",
        name: "Biba Queen — Look 4",
        price: null,
        accent: "#FF007F",
        packshot: null,
        model: null,
        images: [
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/0brv68nn_Basic_%20105.webp",
          "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/iwm6ilpe_Basic_%20127.webp",
        ],
      },
    ],
  },

  /* ---------- Lost in Museum (2024) — video + sketchbook stubs ---------- */
  "lost-in-museum": {
    hero: {
      image:
        "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/vl6mwpk0_IMG_7572.jpeg",
      caption: "Little V&A Exhibition — silver-taped figure · installation view",
    },
    feature: {
      image:
        "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/kzwv7i8v_IMG_7573.jpeg",
      label: "Performance art",
      caption: "Audience gathering around the silver-taped figure · exhibition documentation",
    },
    secondary: {
      image:
        "https://customer-assets.emergentagent.com/job_portfolio-showcase-2301/artifacts/emfe2z9y_%E1%84%8B%E1%85%B5%E1%84%86%E1%85%B5%E1%84%8C%E1%85%B5%202024.%2012.%2010.%20%E1%84%8B%E1%85%A9%E1%84%92%E1%85%AE%207.00%20%281%29.jpg",
      caption: "Studio fitting · 2024",
    },
    description: [
      "A capsule wandering the wrong wing of the Met — Rococo gowns crashed by 90s K-pop sportswear and a glass-cracking lime.",
      "This project treats museum silence as a runway rule to break — texture on texture, high archive next to high street, and a girl who signed in at the front desk but never left the gift shop.",
    ],
    credit: "Mia Cho",
    video: {
      eyebrow: "Film · Moving image",
      title: null,
      url: null,
      placeholder: "Lost in Museum — film",
      note: "Runway film and behind-the-scenes edit will live here. Add a YouTube/Vimeo link or upload an MP4.",
      caption: null,
    },
    process: {
      eyebrow: "Sketchbook · Process",
      heading: {
        before: "Wrong wing,",
        accent: "right dress",
        accentColor: "lime",
        after: ".",
      },
      intro:
        "Process pages, mood references, and material tests will appear here — same carousel format as Highschool Fantasy & Biba Queen. Placeholder plates are reserved below.",
      images: [
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/600oexkl_IMG_2932.webp",
          caption: "Reference · Edward Hodges Baily 'Eve Listening to the Voice of Adam' (1842) · silver-taped figure recreation",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/v32v70en_IMG_2933.webp",
          caption: "Process of body casting · sculpturizing oneself — Deconstruct · Explore · Reconstruct + deconstruction study",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/lgpy52en_IMG_2934.webp",
          caption: "'The bird fights its way out of the egg' — Hermann Hesse, Demian · plastic-wrap + duct-tape body-casted suit before breaking down to 2D",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/e8er0jcz_IMG_2935.webp",
          caption: "Sculpture Body Suit ② · Lower body pattern (16 pieces)",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/4mefc6yj_IMG_2936.webp",
          caption: "Sculpture Body Suit ① · Upper body pattern (12 pieces)",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/efzpy8qf_IMG_2939.webp",
          caption: "Whole body suit · left / right · upper + lower body symmetry studies + first-toile fittings",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/9s0bzegv_IMG_2940.webp",
          caption: "3D experiment with original pattern · Deconstruction · Experiment · Reconstruction — hoodie development",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/0ko96pdd_IMG_2941.webp",
          caption: "Skin / toile experiment — belt pattern development, add pants inside before scaling",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/cznruxet_IMG_2943.webp",
          caption: "Final construction · pig-skin fur + hoodie + raw-cut edges · invisible zip + assembly notes",
        },
        {
          src: "https://customer-assets-eiarnc6j.emergentagent.net/job_portfolio-showcase-2301/artifacts/nwmiu9zi_%E1%84%8B%E1%85%B5%E1%84%86%E1%85%B5%E1%84%8C%E1%85%B5%2006-01-2025%2011.04.jpg",
          caption: "Final shoot · London street + green-screen composite · 5 back views",
        },
      ],
      footnote: "Pages coming soon · Mia Cho atelier",
    },
    shop: [],
  },
};
